import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import {
  BarChart,
  Notifications,
  TrendingUp,
  AttachMoney,
  Refresh,
  Warning,
  CheckCircle,
  Speed,
  Payment,
  Security,
  Business,
  Assignment,
} from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

const Dashboard: React.FC = () => {
  // Sample data for the usage trend chart
  const usageData = {
    series: [
      {
        name: 'API Calls',
        data: [1200, 1350, 1100, 1400, 1600, 1800, 2000, 1900, 2100, 2300, 2500, 2400]
      }
    ],
    options: {
      chart: {
        type: 'line' as const,
        height: 200,
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: 'smooth' as const,
        width: 3,
        colors: ['#f44336']
      },
      grid: {
        borderColor: '#e0e0e0',
        strokeDashArray: 3,
      },
      xaxis: {
        categories: ['00:00', '02:00', '04:00', '06:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00', '22:00'],
        labels: {
          style: {
            colors: '#666',
            fontSize: '10px'
          }
        }
      },
      yaxis: {
        labels: {
          style: {
            colors: '#666',
            fontSize: '10px'
          }
        }
      },
      colors: ['#f44336'],
      tooltip: {
        theme: 'light',
        y: {
          formatter: function(val: number) {
            return val + ' calls';
          }
        }
      }
    } as ApexOptions
  };

  return (
    <Box sx={{ flexGrow: 1, width: '100%' }}>
      {/* Main Dashboard Grid */}
      {/* No extra horizontal padding: rely on Layout only so spacing matches other pages */}
      <Box sx={{ width: '100%', px: 0 }}>
        <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
        {/* Top Row */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BarChart sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2">
                  Today's Overview
                </Typography>
              </Box>
              <List dense sx={{ flexGrow: 1 }}>
                <ListItem>
                  <ListItemText 
                    primary="45 Active Customers"
                    primaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="$4,250 MRR"
                    primaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="125K API calls today"
                    primaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Notifications sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6" component="h2">
                  Alerts (3)
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                {/* Alert 1 - Usage Alert */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  mb: 2, 
                  pb: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    bgcolor: 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    flexShrink: 0
                  }}>
                    <Speed sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                      Acme Corp: 95% usage limit
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      High usage alert - approaching limit
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      2 hours ago
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: 'error.main',
                    ml: 1,
                    flexShrink: 0
                  }} />
                </Box>

                {/* Alert 2 - Payment Alert */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  mb: 2, 
                  pb: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    bgcolor: 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    flexShrink: 0
                  }}>
                    <Payment sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                      TechStart: Payment failed
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Payment processing error
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Yesterday
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: 'error.main',
                    ml: 1,
                    flexShrink: 0
                  }} />
                </Box>

                {/* Alert 3 - Security Alert */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  mb: 2
                }}>
                  <Box sx={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    bgcolor: 'warning.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    flexShrink: 0
                  }}>
                    <Security sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                      DevCorp: Token expires soon
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Token will expire in 3 days
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      3 days ago
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: 'warning.main',
                    ml: 1,
                    flexShrink: 0
                  }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Middle Row */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6" component="h2">
                  Usage Trends
                </Typography>
              </Box>
              <Box sx={{ flexGrow: 1, minHeight: 200 }}>
                <ReactApexChart
                  options={usageData.options}
                  series={usageData.series}
                  type="line"
                  height={200}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AttachMoney sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6" component="h2">
                  Revenue
                </Typography>
              </Box>
              <List dense sx={{ flexGrow: 1 }}>
                <ListItem>
                  <ListItemText 
                    primary="This month: $4,250"
                    primaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Last month: $3,100"
                    primaryTypographyProps={{ variant: 'body1' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ mr: 1 }}>
                          Growth:
                        </Typography>
                        <Chip 
                          label="+37%" 
                          size="small" 
                          color="success"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Box>
                    }
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Bottom Row */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Refresh sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2">
                  Recent Activity
                </Typography>
              </Box>
              
              {/* Timeline Container */}
              <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                <Timeline position="right" sx={{ 
                  p: 0, 
                  m: 0,
                  '& .MuiTimelineItem-root::before': {
                    flex: 'none !important',
                    padding: '0 !important'
                  },
                  '& .MuiTimelineItem-root': {
                    minHeight: 'auto !important'
                  }
                }}>
                  {/* Activity 1 */}
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot sx={{ bgcolor: 'info.main' }}>
                        <Business sx={{ color: 'white', fontSize: 16 }} />
                      </TimelineDot>
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: 2, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          <span>Acme Corp registered a new client as </span>
                          <span style={{ color: 'primary.main', fontWeight: 'medium' }}>Microsoft</span>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          12:33 PM
                        </Typography>
                      </Box>
                    </TimelineContent>
                  </TimelineItem>

                  {/* Activity 2 */}
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot sx={{ bgcolor: 'secondary.main' }}>
                        <Assignment sx={{ color: 'white', fontSize: 16 }} />
                      </TimelineDot>
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: 2, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          <span>TechStart created a new project for </span>
                          <span style={{ color: 'primary.main', fontWeight: 'medium' }}>Microsoft</span>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          12:33 PM
                        </Typography>
                      </Box>
                    </TimelineContent>
                  </TimelineItem>

                  {/* Activity 3 */}
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot sx={{ bgcolor: 'success.main' }}>
                        <AttachMoney sx={{ color: 'white', fontSize: 16 }} />
                      </TimelineDot>
                    </TimelineSeparator>
                    <TimelineContent sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          <span>DevCorp closed a new deal on project </span>
                          <span style={{ color: 'primary.main', fontWeight: 'medium' }}>EVP for Microsoft...</span>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          12:33 PM
                        </Typography>
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                </Timeline>
              </Box>
              
              {/* View All Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto' }}>
                <Button 
                  size="small" 
                  sx={{ 
                    color: 'primary.main', 
                    textTransform: 'none',
                    '&:hover': { bgcolor: 'transparent' }
                  }}
                >
                  View All
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Warning sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6" component="h2">
                  System Health
                </Typography>
              </Box>
              <List dense sx={{ flexGrow: 1 }}>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="All systems: Operational"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Database: 2ms avg"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="API: 45ms avg response"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
        </Box>
    </Box>
  );
};

export default Dashboard; 